import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

const services = [
  {
    id: "consulting",
    num: "I",
    title: "Business Consulting",
    tag: "Strategy & Operations",
    lede: "From market entry to organizational design, we help leadership teams make the moves that matter.",
    items: [
      "Strategic planning & vision",
      "Operational efficiency & process design",
      "Financial modeling & unit economics",
      "M&A support & due diligence",
      "Change management & org design",
      "Fractional leadership (CxO advisory)",
    ],
    dark: false,
  },
  {
    id: "ai",
    num: "II",
    title: "AI Strategy, Training & Consulting",
    tag: "Artificial Intelligence · New",
    lede: "Turn AI from a headline into a competitive advantage — with a plan, the training, and the pilots to prove it.",
    items: [
      "AI opportunity assessment & roadmap",
      "Executive & team AI training workshops",
      "Prompt engineering & workflow design",
      "Custom GPT / agent implementation",
      "Data readiness & governance advisory",
      "Responsible AI policy & change management",
    ],
    dark: true,
  },
  {
    id: "marketing",
    num: "III",
    title: "Marketing & Branding",
    tag: "Brand & Growth",
    lede: "Positioning, identity, and go-to-market that turn attention into revenue.",
    items: [
      "Brand strategy & positioning",
      "Visual identity & design systems",
      "Content strategy & copywriting",
      "Digital marketing & campaigns",
      "Website & digital experience",
      "Analytics & performance dashboards",
    ],
    dark: false,
  },
  {
    id: "custom",
    num: "IV",
    title: "Custom Solutions",
    tag: "Tailored engagements",
    lede: "When the challenge is unique, so is the solution. We shape engagements to fit the moment.",
    items: [
      "Leadership workshops & offsites",
      "Executive coaching",
      "Board & investor prep",
      "Interim leadership placements",
      "Growth diagnostics",
      "Technology & tooling advisory",
    ],
    dark: true,
  },
];

const process = [
  { k: "I", t: "Discover", d: "We listen first. Deep-dive interviews, data review, and market context." },
  { k: "II", t: "Diagnose", d: "We name the real problem — not just the symptom presented to us." },
  { k: "III", t: "Design", d: "We craft a plan that fits your team's capacity, culture, and ambition." },
  { k: "IV", t: "Deploy", d: "We work alongside you until the outcome is real — not just recommended." },
];

export default function LuxeServices() {
  return (
    <div data-testid="luxe-services">
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-20">
        <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
          Services
        </div>
        <h1 className="luxe-hero font-serif-display text-[#0a1f5c] font-medium max-w-6xl">
          Built to solve
          <br />
          <em className="italic text-[#b8964f]">real problems,</em> not sell decks.
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl font-fraunces text-[#3a3a37] leading-relaxed">
          Four practices, one operating philosophy: work close to the business,
          leave it better than we found it.
        </p>
      </section>

      <section className="border-t border-[#e6dbc7]">
        {services.map((s) => (
          <div
            key={s.id}
            data-testid={`luxe-service-detail-${s.id}`}
            className={`${
              s.dark
                ? "bg-[#0a1f5c] text-[#f7f2ea]"
                : "bg-[#fbf8f3] text-[#0a1f5c]"
            } border-b ${s.dark ? "border-[#0a1f5c]" : "border-[#e6dbc7]"}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
              <div className="md:col-span-5">
                <div
                  className={`font-fraunces italic text-sm tracking-widest uppercase mb-6 ${
                    s.dark ? "text-[#d4b478]" : "text-[#b8964f]"
                  }`}
                >
                  {s.tag}
                </div>
                <div
                  className={`font-serif-editorial italic text-6xl mb-4 ${
                    s.dark ? "text-[#d4b478]" : "text-[#b8964f]"
                  }`}
                >
                  {s.num}
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
                  {s.title}
                </h2>
                <p
                  className={`mt-6 font-fraunces text-lg leading-relaxed ${
                    s.dark ? "text-[#f0e8d8]/85" : "text-[#3a3a37]"
                  }`}
                >
                  {s.lede}
                </p>
                <Link
                  to="/contact"
                  data-testid={`luxe-service-cta-${s.id}`}
                  className={`mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full font-fraunces text-base transition-all ${
                    s.dark
                      ? "bg-[#d4b478] text-[#0a1f5c] hover:bg-[#f7f2ea]"
                      : "bg-[#0a1f5c] text-[#f7f2ea] hover:bg-[#b8964f] hover:text-[#0a1f5c]"
                  }`}
                >
                  Discuss this engagement <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="md:col-span-7">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {s.items.map((it) => (
                    <li
                      key={it}
                      className={`flex items-start gap-3 border-t pt-4 ${
                        s.dark ? "border-[#f0e8d8]/20" : "border-[#e6dbc7]"
                      }`}
                    >
                      <Check
                        className={`w-4 h-4 mt-1 shrink-0 ${
                          s.dark ? "text-[#d4b478]" : "text-[#b8964f]"
                        }`}
                      />
                      <span
                        className={`font-fraunces text-base md:text-lg ${
                          s.dark ? "text-[#f0e8d8]" : "text-[#0a1f5c]"
                        }`}
                      >
                        {it}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* PROCESS */}
      <section className="bg-[#f0e8d8]/40 border-b border-[#e6dbc7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
            Our approach
          </div>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#0a1f5c] max-w-3xl">
            Four movements.
            <br />
            <em className="italic text-[#b8964f]">Zero theatre.</em>
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-10">
            {process.map((p) => (
              <div key={p.t} className="border-t border-[#b8964f] pt-6">
                <div className="font-serif-editorial italic text-4xl text-[#b8964f]">
                  {p.k}
                </div>
                <h3 className="mt-4 font-serif-display text-2xl md:text-3xl text-[#0a1f5c]">
                  {p.t}
                </h3>
                <p className="mt-3 font-fraunces text-[#3a3a37] leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#fbf8f3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 text-center">
          <h3 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-tight text-[#0a1f5c] max-w-4xl mx-auto">
            Not sure which service?{" "}
            <em className="italic text-[#b8964f]">Let's talk it through.</em>
          </h3>
          <Link
            to="/contact"
            data-testid="luxe-services-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#0a1f5c] text-[#f7f2ea] px-10 py-5 rounded-full font-fraunces text-base hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all"
          >
            Book a free consult <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
